import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader.js';

import { getExtensionFromUrl, replaceFileExtension } from '../../../util/fileType';
// import { RoughnessMipmapper } from 'three/examples/jsm/utils/RoughnessMipmapper';
export type ThreeBgColors = '#373737' | '#f2f2f2';
interface ThreeSceneViewProps {
  gltfUrl: string;
  bgcolor: ThreeBgColors;
  width: number;
  height: number;
  active?: boolean;
}
// 3D视图
export const ThreeSceneView = (props: ThreeSceneViewProps) => {
  let { gltfUrl } = props;
  const { active = true, bgcolor = '#373737', width, height } = props;

  if (!active || width === 0 || height === 0) {
    return null;
  }

  const type = getExtensionFromUrl(gltfUrl);
  const types = ['stl', 'iges', 'stp', 'fbx', 'STL', 'IGES', 'STP', 'FBX'];

  if (types.includes('1111')) {
    gltfUrl = replaceFileExtension(gltfUrl, 'zip');
  }

  const [containerWidth, setContainerWidth] = useState(width);
  const [containerHeight, setContainerHeight] = useState(height);

  const fileMap = {};
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const controlsRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const widthRef = useRef(0);
  const heightRef = useRef(0);

  useEffect(() => {
    window.addEventListener('resize', resetSize);
    initThree();
    animate();
    if (type==='zip') {
      loadZipFile(gltfUrl).then(async res => {
        await fileToBlob(gltfUrl, res, fileMap);
        loadGLTF();
      });
    } else {
      loadGLTF();
    }

    return () => {
      rendererRef?.current?.dispose();
      window.removeEventListener('resize', resetSize);
    };
  }, []);

  const loadZipFile = (url) => {
    return new Promise(resolve => {
      const fileLoader = new THREE.FileLoader();
      fileLoader
      .setResponseType("arraybuffer")
      .load(
        url,
        data => {
          resolve(data);
        },
      );
    });
  };

  const fileToBlob = async (url, data, fileMap) => {
    //zip.js加载文件流生成对应文件:
    const zip = new JSZip();
    const promise = JSZip.external.Promise;
    const baseUrl = 'blob:' + THREE.LoaderUtils.extractUrlBase(url);
    const pendings = [];
    await zip.loadAsync(data);
    //转成blob文件，用URL.createObjectURL创建文件的url
    for (let file in zip.files) {
      const entry = zip.file(file);
      if (entry === null) continue;
      pendings.push(entry.async('blob').then(((file, blob) => {
        fileMap[baseUrl + file] = URL.createObjectURL(blob);
      }).bind(this, file)))
    }
    //监听所有请求结束
    await promise.all(pendings);
  };

  const findFile = (fileMap) => {
    //模型文件url
    return Object.keys(fileMap).find(item => /\.(gltf)$/.test(item));
  };

  const initThree = () => {
    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    // 场景
    const scene = new THREE.Scene();

    // 默认相机
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 0, 0.1);

    // 渲染器
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      logarithmicDepthBuffer: true, // 解决远近极端位置时的深度冲突
      precision: 'highp'     
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMappingExposure = 1;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.setClearColor(bgcolor, 1); // 背景颜色
    containerRef.current.appendChild(renderer.domElement);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 环境光照
    // const light = new THREE.AmbientLight('#fafafa');
    // scene.add(light);

    // 平行光
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5); // 光源位置
    directionalLight.castShadow = true; // 关键：允许光源投射阴影

    // 可选：优化阴影质量（根据模型大小调整）
    directionalLight.shadow.mapSize.width = 2048; // 阴影贴图分辨率（越大越清晰，性能消耗越高）
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5; // 阴影相机近截面（避免裁剪模型）
    directionalLight.shadow.camera.far = 20; // 阴影相机远截面（覆盖模型范围）
    scene.add(directionalLight);

    // 控制器
    const orbitControls = new OrbitControls(camera, renderer.domElement);
    // orbitControls.minDistance = 2;
    // orbitControls.maxDistance = 10;
    // orbitControls.target.set(0, 0, -0.2);
    orbitControls.update();

    // 保存变量
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = orbitControls;
    widthRef.current = width;
    heightRef.current = height;
  };

  const animate = () => {
    requestAnimationFrame(animate);
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  const loadGLTF = () => {
    // 环境光照贴图，暂时先不加载 royal_esplanade_1k.hdr
    new RGBELoader().load('/images/quarry_01_1k.hdr', function (texture) {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      // sceneRef.current.background = texture;
      sceneRef.current.environment = texture;
      const manager = new THREE.LoadingManager();
      //转换处理，传入的是后台返回的路径，需找到对应blob
      manager.setURLModifier(url1 => {
        return fileMap[url1] ? fileMap[url1] : url1;
      });
      const dracoLoader=new DRACOLoader();

      const loader = type === 'zip' ? new GLTFLoader(manager) : new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);
      loader.load(type==='zip' ? findFile(fileMap) : gltfUrl, function (gltf) {

        gltf.scene.traverse(function (child) {
          if (child.isMesh) {
            // 强制使用 PBR 材质（若自动解析失败）
            child.material = new THREE.MeshPhysicalMaterial({
              map: child.material.map, // 保留原纹理
              metalness: child.material.metalness, // 继承原金属度
              roughness: child.material.roughness, // 继承原粗糙度
            });
          }
        });
        sceneRef.current.add(gltf.scene);

        // 给模型添加一个外框来测量模型的大小尺寸，以便调整相机的距离
        const bbox = new THREE.Box3().setFromObject(gltf.scene);
        
        const center = new THREE.Vector3();
        bbox.getCenter(center);

        const size = bbox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fovRad = cameraRef.current.fov * (Math.PI / 180);
        const cameraDistance = maxDim / (2 * Math.tan(fovRad / 2));
        cameraRef.current.position.set(
          center.x,
          center.y,
          center.z + cameraDistance * 1.5
        );
        // 根据当前模型的最大边长，调整相机的距离
        // cameraRef.current.position.set(0, 0, maxSize / Math.sin(17.5 * Math.PI / 180));
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      });
    });
  };

  const onWindowResize = () => {
    cameraRef.current.aspect = widthRef.current / heightRef.current;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(widthRef.current, heightRef.current);
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  const resetSize = () => {
    const width = rootRef.current?.offsetWidth;
    const height = rootRef.current?.offsetHeight;
    setContainerWidth(width);
    setContainerHeight(height);
    widthRef.current = width;
    heightRef.current = height;
    onWindowResize();
  };

  return (
    <div ref={rootRef} style={{ width: '100%', height: '100%' }}>
      <div ref={containerRef}
        style={{
          position: 'relative',
          width: containerWidth,
          height: containerHeight
        }}
      />
    </div>
  );
};
